import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Button, Grid, Paper, Select, TextField,
    FormControl, InputLabel, LinearProgress, IconButton, Tooltip,
    Table, TableBody, TableCell, TableContainer, TableRow, TableHead, Chip,
    Alert, Tabs, Tab
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

/* ─── Extensiones / tipos válidos para imágenes médicas ─── */
const EXTENSIONES_DICOM = ['.dcm', '.dicom', '.dic', '.dc3', '.ima'];
const EXTENSIONES_IMAGEN = ['.jpg', '.jpeg', '.png', '.tiff', '.tif', '.bmp'];
const TODAS_EXTENSIONES = [...EXTENSIONES_DICOM, ...EXTENSIONES_IMAGEN];

/* ─── Mapeo de nombres comunes del equipo DRGEM a proyecciones ─── */
const MAPEO_PROYECCIONES = {
    'PA':         'PA',
    'AP':         'AP',
    'LAT':        'LAT',
    'LATERAL':    'LAT',
    'OBL':        'OBL',
    'OBLICUA':    'OBL',
    'PA-L':       'PA-L',
    'AP-L':       'AP-L',
};

const MAPEO_ESTUDIOS = {
    'TORAX':     'CHEST',
    'CHEST':     'CHEST',
    'ABDOMEN':   'ABDOMEN',
    'PELVIS':    'PELVIS',
    'COLUMNA':   'SPINE',
    'ESCAPULA':  'SCAPULA',
    'CLAVÍCULA': 'CLAVICLE',
    'CLAVICULA': 'CLAVICLE',
    'CRÁNEO':    'SKULL',
    'CRANEO':    'SKULL',
    'CADERA':    'HIP',
    'RODILLA':   'KNEE',
    'TOBILLO':   'ANKLE',
    'PIE':       'FOOT',
    'MANO':      'HAND',
    'MUÑECA':    'WRIST',
    'MUNECA':    'WRIST',
    'HOMBRO':    'SHOULDER',
    'MUSLO':     'FEMUR',
    'FEMUR':     'FEMUR',
    'TIBIA':     'TIBIA',
    'HUMERO':    'HUMERUS',
    'CODO':      'ELBOW',
    'SENOS':     'SINUSES',
    'COSTILLAS': 'RIBS',
};

/* Detectar proyección y estudio desde el nombre de archivo/carpeta */
function detectarDesdNombre(nombre) {
    const upper = nombre.toUpperCase().replace(/[_\-\.]/g, ' ');
    let proyeccion = '';
    let estudioDetectado = '';
    let lado = '';

    // Detectar proyección
    for (const [key, val] of Object.entries(MAPEO_PROYECCIONES)) {
        if (upper.includes(key)) {
            proyeccion = val;
            break;
        }
    }

    // Detectar tipo de estudio
    for (const [key, val] of Object.entries(MAPEO_ESTUDIOS)) {
        if (upper.includes(key)) {
            estudioDetectado = val;
            break;
        }
    }

    // Detectar lado
    if (upper.includes('DE PIE') || upper.includes('ERECTO')) {
        // "DE PIE" es posición, no lateralidad
    } else if (upper.includes(' IZQ') || upper.includes('LEFT') || upper.includes(' L ')) {
        lado = 'L';
    } else if (upper.includes(' DER') || upper.includes('RIGHT') || upper.includes(' R ')) {
        lado = 'R';
    }

    return { proyeccion, estudioDetectado, lado };
}

/* Verificar si un archivo es probablemente DICOM (sin extensión) */
function esPosibleDicom(file) {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (EXTENSIONES_DICOM.includes(ext)) return 'dicom';
    if (EXTENSIONES_IMAGEN.includes(ext)) return 'imagen';
    // Sin extensión conocida → podría ser DICOM (el equipo DRGEM a veces no pone extensión)
    if (!file.name.includes('.') || file.size > 100000) return 'posible_dicom';
    return 'desconocido';
}

function DicomUpload() {
    const { tienePermiso } = useAccesos();
    const { user } = useUser();
    const server = process.env.REACT_APP_API_ROUTE;
    const [searchParams] = useSearchParams();
    const estudioId = searchParams.get('estudioId');
    const detalleId = searchParams.get('detalleId');

    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const [detalleInfo, setDetalleInfo] = useState(null);
    const [archivos, setArchivos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progreso, setProgreso] = useState(0);
    const [instanciasSubidas, setInstanciasSubidas] = useState([]);
    const [proyecciones, setProyecciones] = useState([]);
    const [lados, setLados] = useState([]);
    const [tiposEntrega, setTiposEntrega] = useState([]);
    const [tabCarga, setTabCarga] = useState(0); // 0=archivos  1=carpeta

    // Campos para cada archivo
    const [archivosConfig, setArchivosConfig] = useState([]);

    // Para crear estudio nuevo si no existe
    const [idEstudio, setIdEstudio] = useState(estudioId || '');
    const [idTipoEntrega, setIdTipoEntrega] = useState('');

    const fetchCatalogos = async () => {
        // Cargar cada catálogo de forma independiente para que si uno falla, los otros sigan
        try {
            const proyRes = await fetch(`${server}/ImgProyecciones`);
            if (proyRes.ok) {
                const proyData = await proyRes.json();
                setProyecciones(proyData.$values || proyData || []);
            } else {
                console.warn('ImgProyecciones respondió:', proyRes.status);
            }
        } catch (e) { console.error('Error catálogo proyecciones:', e); }

        try {
            const ladosRes = await fetch(`${server}/ImgLados`);
            if (ladosRes.ok) {
                const ladosData = await ladosRes.json();
                setLados(ladosData.$values || ladosData || []);
            } else {
                console.warn('ImgLados respondió:', ladosRes.status);
            }
        } catch (e) { console.error('Error catálogo lados:', e); }

        try {
            const tiposRes = await fetch(`${server}/ImgTiposDeEntrega`);
            if (tiposRes.ok) {
                const tiposData = await tiposRes.json();
                const lista = tiposData.$values || tiposData || [];
                console.log('Tipos de entrega cargados:', lista);
                setTiposEntrega(lista);
            } else {
                console.warn('ImgTiposDeEntrega respondió:', tiposRes.status);
            }
        } catch (e) { console.error('Error catálogo tipos entrega:', e); }
    };

    const fetchDetalleInfo = async () => {
        if (!detalleId || detalleId === 'undefined') return;
        try {
            const response = await fetch(`${server}/MgOrdenesDeTrabajoDetalles/imagenes/detalle-completo/${detalleId}`);
            if (response.ok) {
                const data = await response.json();
                setDetalleInfo(data);
                // Si el detalle ya tiene estudio, usarlo
                if (!idEstudio && data.estudios && data.estudios.$values) {
                    const estudios = data.estudios.$values;
                    if (estudios.length > 0) {
                        setIdEstudio(estudios[0].idEstudio);
                    }
                }
            }
        } catch (error) {
            console.error('Error al cargar detalle:', error);
        }
    };

    const fetchInstanciasSubidas = async () => {
        if (!idEstudio) return;
        try {
            const response = await fetch(`${server}/ImgInstanciasDicom/porestudio/${idEstudio}`);
            const data = await response.json();
            setInstanciasSubidas(data.$values || data || []);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    useEffect(() => {
        fetchCatalogos();
        fetchDetalleInfo();
        if (idEstudio) fetchInstanciasSubidas();
    }, [idEstudio]);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        procesarArchivos(files);
    };

    const handleFolderSelect = (e) => {
        const files = Array.from(e.target.files);
        procesarArchivos(files);
    };

    const procesarArchivos = (files) => {
        // Aceptar DICOM, imágenes y archivos sin extensión (posible DICOM del DRGEM)
        const validos = files.filter(f => {
            const tipo = esPosibleDicom(f);
            return tipo !== 'desconocido';
        });

        if (validos.length === 0) {
            Swal.fire('Validación',
                'No se encontraron archivos válidos.\n\nFormatos aceptados: .dcm, .dicom, .ima, .jpg, .png, .tiff, .bmp\nTambién se aceptan archivos sin extensión (DICOM del equipo DRGEM).',
                'warning'
            );
            return;
        }

        // Auto-detectar proyección y lado desde los nombres
        const configs = validos.map(f => {
            const rutaCompleta = f.webkitRelativePath || f.name;
            const deteccion = detectarDesdNombre(rutaCompleta);
            // Buscar ID de proyección que coincida
            const proyMatch = proyecciones.find(p =>
                p.descripcion?.toUpperCase().includes(deteccion.proyeccion) ||
                p.descripcion?.toUpperCase() === deteccion.proyeccion
            );
            const ladoMatch = lados.find(l =>
                l.descripcion?.toUpperCase() === deteccion.lado ||
                l.descripcion?.toUpperCase().startsWith(deteccion.lado === 'L' ? 'IZQ' : deteccion.lado === 'R' ? 'DER' : '___')
            );
            return {
                idProyeccion: proyMatch?.id || '',
                idLado: ladoMatch?.id || '',
                tipoArchivo: esPosibleDicom(f),
                estudioDetectado: deteccion.estudioDetectado,
                rutaRelativa: f.webkitRelativePath || '',
            };
        });

        setArchivos(validos);
        setArchivosConfig(configs);

        // Mostrar resumen
        const nDicom = configs.filter(c => c.tipoArchivo === 'dicom' || c.tipoArchivo === 'posible_dicom').length;
        const nImagen = configs.filter(c => c.tipoArchivo === 'imagen').length;
        const autoDetectados = configs.filter(c => c.idProyeccion || c.idLado).length;

        if (validos.length > 0) {
            Swal.fire({
                title: `${validos.length} archivo(s) encontrado(s)`,
                html: `
                    <div style="text-align:left; font-size:14px">
                        ${nDicom > 0 ? `<p>📋 <b>${nDicom}</b> archivo(s) DICOM</p>` : ''}
                        ${nImagen > 0 ? `<p>🖼️ <b>${nImagen}</b> imagen(es) (JPG/PNG/TIFF)</p>` : ''}
                        ${autoDetectados > 0 ? `<p>✅ <b>${autoDetectados}</b> con proyección/lado auto-detectado</p>` : ''}
                        <p style="color:#888;font-size:12px">Verifique la proyección y lado de cada archivo antes de subir.</p>
                    </div>
                `,
                icon: 'info',
                confirmButtonText: 'Continuar'
            });
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        // Intentar obtener archivos de carpetas arrastradas
        if (e.dataTransfer.items) {
            const allFiles = [];
            const entries = [...e.dataTransfer.items]
                .filter(item => item.kind === 'file')
                .map(item => item.webkitGetAsEntry?.() || item.getAsFile());

            // Si hay entries con entryRead, recorrer la carpeta
            const readEntries = async (entry) => {
                if (entry.isFile) {
                    return new Promise(resolve => entry.file(f => resolve([f])));
                } else if (entry.isDirectory) {
                    const reader = entry.createReader();
                    return new Promise(resolve => {
                        reader.readEntries(async (entries) => {
                            const files = [];
                            for (const e of entries) {
                                const subFiles = await readEntries(e);
                                files.push(...subFiles);
                            }
                            resolve(files);
                        });
                    });
                }
                return [];
            };

            const processEntries = async () => {
                for (const entry of entries) {
                    if (entry && entry.isDirectory) {
                        const files = await readEntries(entry);
                        allFiles.push(...files);
                    } else if (entry && entry.isFile) {
                        const files = await readEntries(entry);
                        allFiles.push(...files);
                    } else if (entry instanceof File) {
                        allFiles.push(entry);
                    }
                }
                procesarArchivos(allFiles);
            };
            processEntries();
        } else {
            const files = Array.from(e.dataTransfer.files);
            procesarArchivos(files);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleConfigChange = (index, field, value) => {
        setArchivosConfig(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleCrearEstudio = async () => {
        if (!detalleId) {
            Swal.fire('Error', 'Se requiere un ID de detalle de orden', 'error');
            return;
        }
        if (!idTipoEntrega) {
            Swal.fire('Validación', 'Seleccione un tipo de entrega', 'warning');
            return;
        }
        try {
            const response = await fetch(`${server}/ImgEstudiosDicom`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idDetalle: parseInt(detalleId),
                    idTipoEntrega: parseInt(idTipoEntrega),
                    estado: 'pendiente',
                    ingresadoPor: user.codigoUsuario.padEnd(20)
                })
            });
            if (response.ok) {
                const data = await response.json();
                setIdEstudio(data.idEstudio || data.id);
                Swal.fire('Éxito', 'Estudio DICOM creado', 'success');
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'Error al crear estudio', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleUpload = async () => {
        if (!tienePermiso('Subir Archivo DICOM')) return;
        if (!idEstudio) {
            Swal.fire('Validación', 'Primero cree o seleccione un estudio', 'warning');
            return;
        }
        if (archivos.length === 0) {
            Swal.fire('Validación', 'Seleccione archivos para subir', 'warning');
            return;
        }

        setUploading(true);
        setProgreso(0);
        let subidos = 0;
        let existentes = 0;
        let fallidos = 0;
        let duplicadosOtroEstudio = 0;

        for (let i = 0; i < archivos.length; i++) {
            try {
                const formData = new FormData();
                formData.append('file', archivos[i]);
                formData.append('idEstudio', idEstudio);
                if (archivosConfig[i]?.idProyeccion) {
                    formData.append('idProyeccion', archivosConfig[i].idProyeccion);
                }
                if (archivosConfig[i]?.idLado) {
                    formData.append('idLado', archivosConfig[i].idLado);
                }
                formData.append('usuario', user.codigoUsuario.padEnd(20));

                const response = await fetch(`${server}/ImgInstanciasDicom/upload`, {
                    method: 'POST',
                    body: formData
                });

                if (response.ok) {
                    // Respuesta idempotente: puede venir como ok normal o duplicado ya existente.
                    let payload = null;
                    try {
                        payload = await response.json();
                    } catch {
                        payload = null;
                    }

                    if (payload?.Duplicado === true || payload?.duplicado === true) {
                        const idEstudioPayload = payload?.IdEstudio ?? payload?.idEstudio;
                        const mismoEstudio = payload?.MismoEstudio === true
                            || payload?.mismoEstudio === true
                            || String(idEstudioPayload) === String(idEstudio);

                        if (mismoEstudio) {
                            existentes++;
                        } else {
                            duplicadosOtroEstudio++;
                            fallidos++;
                            console.warn(`Archivo ${archivos[i].name}: DICOM ya existe en otro estudio (${idEstudioPayload ?? 'N/A'}).`);
                        }
                    } else {
                        subidos++;
                    }
                } else if (response.status === 409) {
                    // Si el backend devuelve 409, se considera no subido para evitar falsos exitos.
                    fallidos++;
                    let msg = '';
                    try {
                        msg = await response.text();
                    } catch {
                        msg = '';
                    }
                    console.warn(`Archivo ${archivos[i].name}: no subido (${msg || '409 Conflict'})`);
                } else {
                    fallidos++;
                    let msg = '';
                    try {
                        msg = await response.text();
                    } catch {
                        msg = '';
                    }
                    console.error(`Error subiendo ${archivos[i].name}: HTTP ${response.status} ${msg}`);
                }
            } catch (error) {
                fallidos++;
                console.error(`Error subiendo ${archivos[i].name}:`, error);
            }
            setProgreso(Math.round(((i + 1) / archivos.length) * 100));
        }

        setUploading(false);
        setArchivos([]);
        setArchivosConfig([]);
        fetchInstanciasSubidas();

        const procesados = subidos + existentes;

        if (procesados === archivos.length && fallidos === 0) {
            const detalleDuplicados = existentes > 0 ? ` (${existentes} ya existían)` : '';
            Swal.fire('Éxito', `${procesados} de ${archivos.length} archivo(s) procesado(s)${detalleDuplicados}`, 'success');
        } else {
            Swal.fire('Advertencia',
                `${procesados} de ${archivos.length} archivo(s) procesado(s). ` +
                `Nuevos: ${subidos}, Ya existentes: ${existentes}, Fallidos: ${fallidos}` +
                (duplicadosOtroEstudio > 0 ? `. Duplicados en otro estudio: ${duplicadosOtroEstudio}` : ''),
                'warning'
            );
        }
    };

    const handleEliminarInstancia = async (instancia) => {
        if (!tienePermiso('Eliminar Imagen DICOM')) return;
        const result = await Swal.fire({
            title: '¿Eliminar imagen?',
            text: `Se eliminará la instancia ${instancia.sopInstanceUid || instancia.id}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (!result.isConfirmed) return;

        try {
            const response = await fetch(
                `${server}/ImgInstanciasDicom/${instancia.id}/${user.codigoUsuario.padEnd(20)}/`,
                { method: 'DELETE' }
            );
            if (response.ok) {
                fetchInstanciasSubidas();
                Swal.fire('Éxito', 'Imagen eliminada', 'success');
            } else {
                Swal.fire('Error', 'Error al eliminar', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    return (
        <div className="right_col" role="main">
        <div className="x_content">
            <div className="x_title">
                <h2>Carga de Imágenes DICOM</h2>
            </div>

            {/* Info del detalle */}
            {detalleInfo && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip label={`Detalle #${detalleId}`} size="small" />
                    {detalleInfo.paciente && <span><b>Paciente:</b> {typeof detalleInfo.paciente === 'object' ? detalleInfo.paciente.nombreCompleto : detalleInfo.paciente}</span>}
                    {detalleInfo.producto && <span><b>Examen:</b> {typeof detalleInfo.producto === 'object' ? (detalleInfo.producto.nombre || detalleInfo.producto.descripcion) : detalleInfo.producto}</span>}
                    {detalleInfo.nombrePaciente && !detalleInfo.paciente && <span><b>Paciente:</b> {detalleInfo.nombrePaciente}</span>}
                    {detalleInfo.nombreProducto && !detalleInfo.producto && <span><b>Examen:</b> {detalleInfo.nombreProducto}</span>}
                    {detalleInfo.estadoFlujo && <Chip label={detalleInfo.estadoFlujo} size="small" color={detalleInfo.estadoFlujo === 'pendiente' ? 'warning' : 'info'} />}
                </Box>
            )}

            {/* Crear estudio si no hay uno */}
            {!idEstudio && (
                <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, mb: 2 }}>
                    <Typography variant="h6">Crear Estudio DICOM</Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="ID Detalle Orden"
                                value={detalleId || ''}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel shrink>Tipo de Entrega</InputLabel>
                                <Select
                                    native
                                    value={idTipoEntrega}
                                    onChange={(e) => setIdTipoEntrega(e.target.value)}
                                    label="Tipo de Entrega"
                                >
                                    <option value="">-- Seleccione --</option>
                                    {tiposEntrega.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.descripcion}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button variant="contained" onClick={handleCrearEstudio}>
                                Crear Estudio
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Zona de carga: Archivos o Carpeta */}
            {idEstudio && (
                <>
                    <Alert severity="info" sx={{ mb: 1 }}>
                        <Typography variant="body2">
                            <b>Formatos aceptados:</b> Archivos DICOM (.dcm, .dicom, .ima), imágenes (.jpg, .png, .tiff, .bmp) y archivos sin extensión del equipo DRGEM.
                            Puede arrastrar carpetas completas del equipo de rayos X.
                        </Typography>
                    </Alert>

                    <Tabs value={tabCarga} onChange={(_, v) => setTabCarga(v)} sx={{ mb: 1 }}>
                        <Tab icon={<CloudUploadIcon />} label="Archivos individuales" iconPosition="start" />
                        <Tab icon={<FolderOpenIcon />} label="Carpeta del equipo" iconPosition="start" />
                    </Tabs>

                    {/* Tab 0: Archivos individuales */}
                    {tabCarga === 0 && (
                        <Paper
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                p: 4, mb: 2, textAlign: 'center',
                                border: '2px dashed #1976d2', borderRadius: 2,
                                cursor: 'pointer', bgcolor: '#f8fbff',
                                '&:hover': { bgcolor: '#e3f2fd', borderColor: '#1565c0' },
                                transition: 'all 0.2s'
                            }}
                        >
                            <CloudUploadIcon sx={{ fontSize: 48, color: '#1976d2' }} />
                            <Typography variant="h6" color="primary">
                                Arrastre archivos aquí o haga clic para seleccionar
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                DICOM (.dcm, .ima), Imágenes (.jpg, .png, .tiff) — Estudio #{idEstudio}
                            </Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept=".dcm,.dicom,.dic,.dc3,.ima,.jpg,.jpeg,.png,.tiff,.tif,.bmp,*/*"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                        </Paper>
                    )}

                    {/* Tab 1: Carpeta completa */}
                    {tabCarga === 1 && (
                        <Paper
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onClick={() => folderInputRef.current?.click()}
                            sx={{
                                p: 4, mb: 2, textAlign: 'center',
                                border: '2px dashed #ff9800', borderRadius: 2,
                                cursor: 'pointer', bgcolor: '#fffbf0',
                                '&:hover': { bgcolor: '#fff3e0', borderColor: '#f57c00' },
                                transition: 'all 0.2s'
                            }}
                        >
                            <FolderOpenIcon sx={{ fontSize: 48, color: '#ff9800' }} />
                            <Typography variant="h6" color="warning.dark">
                                Seleccione la carpeta del paciente
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                Seleccione la carpeta completa que contiene los archivos de rayos X<br />
                                (ej: RX TORAX, RX ABDOMEN DE PIE, etc.)
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                Se detectarán automáticamente las proyecciones desde los nombres de archivos
                            </Typography>
                            <input
                                ref={folderInputRef}
                                type="file"
                                onChange={handleFolderSelect}
                                style={{ display: 'none' }}
                                {...{ webkitdirectory: '', mozdirectory: '', directory: '' }}
                            />
                        </Paper>
                    )}

                    {/* Lista de archivos seleccionados con config */}
                    {archivos.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6">
                                    {archivos.length} archivo(s) seleccionado(s)
                                </Typography>
                                <Button size="small" color="error" variant="outlined" onClick={() => { setArchivos([]); setArchivosConfig([]); }}>
                                    Limpiar todo
                                </Button>
                            </Box>

                            <TableContainer component={Paper} sx={{ mb: 1, maxHeight: 400 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width="5%">#</TableCell>
                                            <TableCell width="30%">Archivo</TableCell>
                                            <TableCell width="10%">Tipo</TableCell>
                                            <TableCell width="10%">Tamaño</TableCell>
                                            <TableCell width="20%">Proyección</TableCell>
                                            <TableCell width="20%">Lado</TableCell>
                                            <TableCell width="5%"></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {archivos.map((archivo, idx) => {
                                            const config = archivosConfig[idx] || {};
                                            const tipoChip = config.tipoArchivo === 'dicom'
                                                ? { label: 'DICOM', color: 'primary' }
                                                : config.tipoArchivo === 'posible_dicom'
                                                    ? { label: 'DICOM?', color: 'warning' }
                                                    : { label: 'IMG', color: 'secondary' };
                                            return (
                                                <TableRow key={idx} hover>
                                                    <TableCell>{idx + 1}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" noWrap title={config.rutaRelativa || archivo.name}>
                                                            {config.rutaRelativa || archivo.name}
                                                        </Typography>
                                                        {config.estudioDetectado && (
                                                            <Chip label={config.estudioDetectado} size="small" variant="outlined" sx={{ mt: 0.25, height: 18, fontSize: 10 }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={tipoChip.label} color={tipoChip.color} size="small" sx={{ height: 22, fontSize: 11 }} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{(archivo.size / 1024 / 1024).toFixed(2)} MB</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <FormControl fullWidth size="small">
                                                            <Select
                                                                native
                                                                value={config.idProyeccion || ''}
                                                                onChange={(e) => handleConfigChange(idx, 'idProyeccion', e.target.value)}
                                                                sx={{ fontSize: 13 }}
                                                            >
                                                                <option value="">N/A</option>
                                                                {proyecciones.map(p => (
                                                                    <option key={p.id} value={p.id}>{p.descripcion}</option>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </TableCell>
                                                    <TableCell>
                                                        <FormControl fullWidth size="small">
                                                            <Select
                                                                native
                                                                value={config.idLado || ''}
                                                                onChange={(e) => handleConfigChange(idx, 'idLado', e.target.value)}
                                                                sx={{ fontSize: 13 }}
                                                            >
                                                                <option value="">N/A</option>
                                                                {lados.map(l => (
                                                                    <option key={l.id} value={l.id}>{l.descripcion}</option>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton size="small" color="error"
                                                            onClick={() => {
                                                                setArchivos(prev => prev.filter((_, i) => i !== idx));
                                                                setArchivosConfig(prev => prev.filter((_, i) => i !== idx));
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {uploading && (
                                <Box sx={{ mb: 1 }}>
                                    <LinearProgress variant="determinate" value={progreso} sx={{ height: 8, borderRadius: 4 }} />
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                                        Subiendo {Math.round(progreso)}% — {archivos.length} archivo(s)
                                    </Typography>
                                </Box>
                            )}

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleUpload}
                                disabled={uploading || !tienePermiso('Subir Archivo DICOM')}
                                startIcon={<CloudUploadIcon />}
                                size="large"
                            >
                                {uploading ? `Subiendo... ${progreso}%` : `Subir ${archivos.length} Archivo(s)`}
                            </Button>
                        </Box>
                    )}

                    {/* Instancias ya subidas */}
                    <div className="ln_solid" />
                    <h3>Imágenes Subidas</h3>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>SOP Instance UID</TableCell>
                                    <TableCell>Proyección</TableCell>
                                    <TableCell>Lado</TableCell>
                                    <TableCell>Dimensiones</TableCell>
                                    <TableCell align="right">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {instanciasSubidas.map((inst) => (
                                    <TableRow key={inst.id}>
                                        <TableCell>{inst.id}</TableCell>
                                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {inst.sopInstanceUid}
                                        </TableCell>
                                        <TableCell>{inst.proyeccion || 'N/A'}</TableCell>
                                        <TableCell>{inst.lado || 'N/A'}</TableCell>
                                        <TableCell>{inst.rows && inst.columns ? `${inst.rows}x${inst.columns}` : 'N/A'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                color="info"
                                                disabled={!tienePermiso('Ver Metadatos DICOM')}
                                                onClick={() => Swal.fire({
                                                    title: 'Metadatos DICOM',
                                                    html: `<pre style="text-align:left;font-size:12px">${JSON.stringify(inst, null, 2)}</pre>`,
                                                    width: 600
                                                })}
                                            >
                                                <InfoIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                disabled={!tienePermiso('Eliminar Imagen DICOM')}
                                                onClick={() => handleEliminarInstancia(inst)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {instanciasSubidas.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No hay imágenes subidas</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </div>
        </div>
    );
}

export default DicomUpload;
